/**
 * The message the API actually sent, not the one axios made up.
 *
 * Every controller answers a refusal with `{ success: false, error: "<why>" }` and a real status —
 * "Group 5: requested 2500 credits, only 1400 available", "dispatching without an order". None of
 * that reaches the screen through `error.message`, which is axios's own "Request failed with status
 * code 422". The server's sentence is the only one that tells a user what to do next.
 *
 * Typed structurally rather than against AxiosError so it also handles a plain fetch/Response shape
 * and anything a hook has already unwrapped.
 */
export function apiErrorMessage(error: unknown, fallback = "Something went wrong."): string {
  const body = (error as { response?: { data?: unknown } } | undefined)?.response?.data;

  if (typeof body === "string" && body.trim()) return body;
  if (body && typeof body === "object") {
    const { error: err, message } = body as { error?: unknown; message?: unknown };
    if (typeof err === "string" && err.trim()) return err;
    if (typeof message === "string" && message.trim()) return message;
  }

  // Axios's own text is the LAST resort: it names a status code and nothing else, so it is only
  // better than showing the user nothing at all.
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** The HTTP status of a failed request, or `undefined` when the error carries none. */
export function apiErrorStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | undefined)?.response?.status;
  return typeof status === "number" ? status : undefined;
}
